import { useMemo } from 'react';
import { UseQueryResult, useQuery } from '@tanstack/react-query';

import { Campaign, CampaignAdGroup, CampaignAdGroupGeoLocation } from '../../../types/Campaign';
import { DspGeoLocation } from '../../../types/DspGeoLocation';
import ApiService from '../../../ApiService';

interface UseProps {
    campaign: Campaign;
}

interface UseState {
    targetQuery: UseQueryResult<DspGeoLocation[]>;
    targetCount: number;
}

const useCampaignAdGroupGeoLocationSummary = ({ campaign }: UseProps): UseState => {
    const targetIds = useMemo(() => {
        return [
            ...new Set(
                campaign.adGroups
                    ?.flatMap((adGroup: CampaignAdGroup) => adGroup.adGroupGeoLocations || [])
                    .map((adGroupTarget: CampaignAdGroupGeoLocation) => adGroupTarget.dspGeoLocationId)
            ),
        ];
    }, [campaign.adGroups]);

    const targetQuery = useQuery<DspGeoLocation[]>({
        queryKey: ['campaign/adGroupGeoLocationSummary', targetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspGeoLocations({
                ids: targetIds,
            });

            return data;
        },
        enabled: !!targetIds.length,
        refetchOnWindowFocus: false,
    });

    const targetCount = useMemo((): number => {
        return targetQuery.data?.length || 0;
    }, [targetQuery.data]);

    return { targetQuery, targetCount };
};

export type { UseProps as CampaignAdGroupGeoLocationSummaryProps };
export type { UseState as CampaignAdGroupGeoLocationSummaryState };
export { useCampaignAdGroupGeoLocationSummary };
